import { LOCAL_STORAGE_KEYS } from '@/constants/storageKeys';
import axios, { AxiosError } from 'axios';

declare module 'axios' {
  export interface AxiosRequestConfig {
    skipAuth?: boolean;
    _retry?: boolean;
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

api.interceptors.request.use(
  (config) => {
    // token이 필요 없으면 skipAuth 플래그 설정
    if ((config as any).skipAuth) {
      return config;
    }
    const token = localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;
    if (!originalRequest) return Promise.reject(error);

    // 401 에러, 첫 번째 재시도
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/api/auth/reissue')
    ) {
      originalRequest._retry = true;

      try {
        // 토큰 재발급
        const reissueRes = await api.post('/api/auth/reissue', null, {
          headers: { 'X-Auth-Method': 'COOKIE' },
          withCredentials: true,
        });

        const newAccessToken = (reissueRes.data as any)?.data?.accessToken;
        if (newAccessToken) {
          localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, newAccessToken);

          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

          // 실패했던 요청 재시도
          return api(originalRequest);
        }
      } catch (reissueError) {
        console.error('토큰 재발급 실패:', reissueError);
        // RefreshToken 만료 → 자동 로그아웃 처리
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE_KEYS.ROLE_TYPE);
        window.location.href = '/ko/login'; // 로그인 페이지로 이동
      }
    }

    return Promise.reject(error);
  }
);
